import { upperFirst } from 'lodash-es';
import {
    ReportInterval,
    ReportTemplateEntityType,
    ReportTemplatePageMarginUnit,
    ReportTemplatePageOrientation,
    ReportTemplatePageSize,
    type ReportTemplateUsage,
} from '@/graphql/types';
import { isReportTemplateEntityType } from './typeGuards';

export enum ReportBuilderTemplatePreset {
    Basic = 'basic',
    IncompatibleElements = 'incompatible_elements',
    TemplateIncludeHost = 'template_include_host',
}

const ENTITY_TYPE_TRANSLATIONS: Readonly<Record<ReportTemplateEntityType, string>> = Object.freeze({
    get [ReportTemplateEntityType.MeasuringPoint]() {
        return gettext('MEASURING_POINT');
    },
    get [ReportTemplateEntityType.Project]() {
        return gettext('PROJECT');
    },
});

const REPORT_TEMPLATE_USAGE_TRANSLATIONS: Readonly<
    Record<ReportTemplateEntityType, Readonly<Record<ReportInterval, string>>>
> = Object.freeze({
    [ReportTemplateEntityType.MeasuringPoint]: Object.freeze({
        get [ReportInterval.Daily]() {
            return gettext('REPORT_TEMPLATE_USAGE_MEASURING_POINT_DAILY');
        },
        get [ReportInterval.Weekly]() {
            return gettext('REPORT_TEMPLATE_USAGE_MEASURING_POINT_WEEKLY');
        },
    }),
    [ReportTemplateEntityType.Project]: Object.freeze({
        get [ReportInterval.Daily]() {
            return gettext('REPORT_TEMPLATE_USAGE_PROJECT_DAILY');
        },
        get [ReportInterval.Weekly]() {
            return gettext('REPORT_TEMPLATE_USAGE_PROJECT_WEEKLY');
        },
    }),
});

export const REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS: Readonly<
    Record<ReportTemplatePageSize, string>
> = Object.freeze({
    get [ReportTemplatePageSize.A4]() {
        return 'A4';
    },
    get [ReportTemplatePageSize.Letter]() {
        return gettext('LETTER');
    },
});

export const REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS: Readonly<
    Record<ReportTemplatePageOrientation, string>
> = Object.freeze({
    get [ReportTemplatePageOrientation.Portrait]() {
        return gettext('PORTRAIT');
    },
    get [ReportTemplatePageOrientation.Landscape]() {
        return gettext('LANDSCAPE');
    },
});

export const REPORT_TEMPLATE_PAGE_MARGIN_UNIT_TRANSLATIONS: Readonly<
    Record<ReportTemplatePageMarginUnit, string>
> = Object.freeze({
    get [ReportTemplatePageMarginUnit.Cm]() {
        return gettext('PAGE_MARGIN_UNIT_CM');
    },
    get [ReportTemplatePageMarginUnit.In]() {
        return gettext('PAGE_MARGIN_UNIT_IN');
    },
});

function normalizeEntityType(value: string): string {
    return value.trim().toUpperCase().replace(/-/g, '_');
}

export function translateEntityType(value: string): string {
    const normalizedValue = normalizeEntityType(value);
    if (isReportTemplateEntityType(normalizedValue)) {
        return ENTITY_TYPE_TRANSLATIONS[normalizedValue];
    }

    return upperFirst(value.toLowerCase().replace(/[_-]/g, ' '));
}

export function translateReportTemplateUsage({
    name,
    entityType,
    interval,
}: Pick<ReportTemplateUsage, 'name' | 'entityType' | 'interval'>): string {
    return interpolate(REPORT_TEMPLATE_USAGE_TRANSLATIONS[entityType][interval], { name }, true);
}
